import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardHeader,
  Divider,
  Link,
  List,
  ListItem,
  CircularProgress,
  TextField,
  Typography,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Tooltip,
} from '@mui/material';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAuth } from '../../../hooks/use-auth';
import { userApi } from '../../../api/user-api';

// Row for editable fields
const EditableRow = ({ icon, label, fieldKey, modify, onDoubleClick, children }) => {
  const [hovered, setHovered] = useState(false);
  const isEditing = modify[fieldKey];

  return (
    <ListItem
      disablePadding
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        px: 3,
        py: 1.25,
        transition: 'background-color 0.15s',
        backgroundColor: hovered && !isEditing ? 'rgba(96,96,184,0.04)' : 'transparent',
        cursor: isEditing ? 'default' : 'pointer',
      }}
      onDoubleClick={!isEditing ? onDoubleClick : undefined}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', gap: 1.5 }}>
        <Box sx={{ mt: 0.2, color: '#6060B8', flexShrink: 0 }}>{icon}</Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{ color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, display: 'block' }}
          >
            {label}
          </Typography>
          {children}
        </Box>
        {hovered && !isEditing && (
          <Tooltip title="Double-cliquez pour modifier" placement="top">
            <EditOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled', mt: 0.5, flexShrink: 0 }} />
          </Tooltip>
        )}
      </Box>
    </ListItem>
  );
};

// Row for read-only fields
const ReadOnlyRow = ({ icon, label, children }) => (
  <ListItem disablePadding sx={{ px: 3, py: 1.25 }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', gap: 1.5 }}>
      <Box sx={{ mt: 0.2, color: '#6060B8', flexShrink: 0 }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{ color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, display: 'block' }}
        >
          {label}
        </Typography>
        {children}
      </Box>
    </Box>
  </ListItem>
);

export const CustomerBasicDetails = (props) => {
  let { customer, setCustomer } = props;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [modify, setModify] = useState({
    "firstname": null,
    "lastname": null,
    "email": null,
    "tlms_id": null,
    "syna_id": null
  });
  const [modifyText, setModifyText] = useState("");
  const [allGroups, setAllGroups] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [openGroupDialog, setOpenGroupDialog] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);

  useEffect(() => {
    const fetchGroups = async () => {
      setLoadingGroups(true);
      try {
        const allGroupsResponse = await userApi.getGroups(user.admin_id);
        setAllGroups(allGroupsResponse);
        if (customer?.details?.id) {
          const userGroupsResponse = await userApi.getUserGroups(user.admin_id, customer.details.id);
          setUserGroups(userGroupsResponse);
          setSelectedGroups(userGroupsResponse);
        }
      } catch (error) {
        console.error("Error fetching groups:", error);
      } finally {
        setLoadingGroups(false);
      }
    };
    fetchGroups();
  }, [customer, user.admin_id]);

  function handleClick(field, value) {
    setModify(modify => ({ ...modify, [field]: true }));
    setModifyText(value);
  }

  async function handleModify(field) {
    setModify(modify => ({ ...modify, [field]: false }));
    let changeCustomer = { ...customer };
    changeCustomer.details[field] = modifyText;
    setCustomer(changeCustomer);
    await userApi.modifyUser(user.admin_id, changeCustomer);
  }

  const handleGroupChange = async () => {
    try {
      for (const group of userGroups) {
        await userApi.removeUserFromGroup(user.admin_id, customer.details.id, group.id);
      }
      for (const group of selectedGroups) {
        await userApi.assignUserToGroup(user.admin_id, customer.details.id, group.id);
      }
      setUserGroups(selectedGroups);
      setOpenGroupDialog(false);
    } catch (error) {
      console.error("Error updating user groups:", error);
    }
  };

  const valueTypography = (text) => (
    <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary', mt: 0.25 }}>
      {text}
    </Typography>
  );

  const editField = (fieldKey, defaultVal, currentVal) => (
    modify[fieldKey] ? (
      <TextField
        size="small"
        focused
        autoFocus
        defaultValue={defaultVal}
        onKeyDown={(e) => { if (e.key === 'Enter') handleModify(fieldKey); }}
        onBlur={() => handleModify(fieldKey)}
        onChange={(e) => setModifyText(e.target.value)}
        sx={{ mt: 0.25 }}
      />
    ) : valueTypography(currentVal)
  );

  return (
    <>
      {customer === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <CardHeader
            avatar={<InfoOutlinedIcon sx={{ color: '#6060B8' }} />}
            title={
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                Informations
              </Typography>
            }
            subheader={
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                Double-cliquez sur un champ pour le modifier
              </Typography>
            }
            sx={{ pb: 1 }}
          />
          <Divider />
          <List disablePadding>

            {/* Prénom */}
            <EditableRow
              icon={<PersonOutlinedIcon fontSize="small" />}
              label="Prénom"
              fieldKey="firstname"
              modify={modify}
              onDoubleClick={() => handleClick('firstname', customer.details.firstname)}
            >
              {editField('firstname', customer.details.firstname, customer.details.firstname)}
            </EditableRow>
            <Divider sx={{ mx: 3 }} />

            {/* Nom */}
            <EditableRow
              icon={<PersonOutlinedIcon fontSize="small" />}
              label="Nom"
              fieldKey="lastname"
              modify={modify}
              onDoubleClick={() => handleClick('lastname', customer.details.lastname)}
            >
              {editField('lastname', customer.details.lastname, customer.details.lastname)}
            </EditableRow>
            <Divider sx={{ mx: 3 }} />

            {/* Email */}
            <EditableRow
              icon={<EmailOutlinedIcon fontSize="small" />}
              label="Email"
              fieldKey="email"
              modify={modify}
              onDoubleClick={() => handleClick('email', customer.details.email)}
            >
              {editField('email', customer.details.email, customer.details.email)}
            </EditableRow>

            {user.id_service === 0 && (
              <>
                {customer.tlms_domain && (
                  <>
                    <Divider sx={{ mx: 3 }} />
                    <EditableRow
                      icon={<LinkOutlinedIcon fontSize="small" />}
                      label="Tlms"
                      fieldKey="tlms_id"
                      modify={modify}
                      onDoubleClick={() => handleClick('tlms_id', customer.details.tlms_id)}
                    >
                      {modify.tlms_id ? (
                        <TextField
                          size="small"
                          focused
                          autoFocus
                          defaultValue={customer.details.tlms_id}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleModify('tlms_id'); }}
                          onBlur={() => handleModify('tlms_id')}
                          onChange={(e) => setModifyText(e.target.value)}
                          sx={{ mt: 0.25 }}
                        />
                      ) : (
                        <Link
                          href={`https://${customer.tlms_domain}/user/info/id:${customer.details.tlms_id}`}
                          target="_blank"
                          underline="hover"
                          sx={{ mt: 0.25, display: 'inline-block', fontWeight: 500, fontSize: '0.875rem' }}
                        >
                          {customer.details.tlms_id === 0 ? "Pas de compte TLMS" : customer.details.tlms_id}
                        </Link>
                      )}
                    </EditableRow>
                  </>
                )}

                {customer.has_syna === 'true' && (
                  <>
                    <Divider sx={{ mx: 3 }} />
                    <EditableRow
                      icon={<BadgeOutlinedIcon fontSize="small" />}
                      label="Syndays"
                      fieldKey="syna_id"
                      modify={modify}
                      onDoubleClick={() => handleClick('syna_id', customer.details.syna_id)}
                    >
                      {modify.syna_id ? (
                        <TextField
                          size="small"
                          focused
                          autoFocus
                          defaultValue={customer.details.syna_id}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleModify('syna_id'); }}
                          onBlur={() => handleModify('syna_id')}
                          onChange={(e) => setModifyText(e.target.value)}
                          sx={{ mt: 0.25 }}
                        />
                      ) : (
                        <Link
                          href={`https://${globalThis.localStorage.getItem('tenant')}.syndays.fr/dashboard/customers/${customer.details.syna_id}`}
                          target="_blank"
                          underline="hover"
                          sx={{ mt: 0.25, display: 'inline-block', fontWeight: 500, fontSize: '0.875rem' }}
                        >
                          {customer.details.syna_id === 0 ? "Pas de compte SYNDAYS" : customer.details.syna_id}
                        </Link>
                      )}
                    </EditableRow>
                  </>
                )}
              </>
            )}

            {/* Dernière activité */}
            <Divider sx={{ mx: 3 }} />
            <ReadOnlyRow icon={<AccessTimeOutlinedIcon fontSize="small" />} label="Dernière activité">
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary', mt: 0.25 }}>
                {customer.details.last_activity_type
                  ? `${customer.details.last_activity_type} le ${customer.details.last_activity_date}`
                  : "Aucune activité"}
              </Typography>
            </ReadOnlyRow>

            {/* Groupes */}
            {user.id_service === 0 && (user.access_level === 0 || user.access_level === 1) && (
              <>
                <Divider sx={{ mx: 3 }} />
                <ReadOnlyRow icon={<GroupOutlinedIcon fontSize="small" />} label="Groupes">
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5, alignItems: 'center' }}>
                    {userGroups.length > 0 ? (
                      userGroups.map(group => (
                        <Chip
                          key={group.id}
                          label={group.nom}
                          size="small"
                          onClick={() => navigate(`/${localStorage.getItem('tenant')}/dashboard/group/${group.id}`)}
                          sx={{
                            backgroundColor: 'rgba(96,96,184,0.1)',
                            color: '#6060B8',
                            fontWeight: 600,
                            '&:hover': { backgroundColor: 'rgba(96,96,184,0.18)' },
                            cursor: 'pointer',
                          }}
                        />
                      ))
                    ) : (
                      <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                        Aucun groupe
                      </Typography>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setOpenGroupDialog(true)}
                      sx={{ ml: 0.5, borderColor: '#6060B8', color: '#6060B8', fontSize: '0.75rem', py: 0.25, '&:hover': { borderColor: '#4a4a9a', backgroundColor: 'rgba(96,96,184,0.06)' } }}
                    >
                      Modifier
                    </Button>
                  </Box>
                </ReadOnlyRow>
              </>
            )}

          </List>

          {/* Dialog groupes */}
          <Dialog open={openGroupDialog} onClose={() => setOpenGroupDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Gérer les groupes</DialogTitle>
            <DialogContent>
              {loadingGroups ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Autocomplete
                  multiple
                  options={allGroups}
                  getOptionLabel={(option) => option.nom}
                  value={selectedGroups}
                  onChange={(_, newValue) => setSelectedGroups(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Sélectionner un ou plusieurs groupes"
                      variant="outlined"
                      fullWidth
                      margin="normal"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip {...getTagProps({ index })} key={option.id} label={option.nom} size="small" />
                    ))
                  }
                />
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
              <Button variant="outlined" onClick={() => setOpenGroupDialog(false)}>Annuler</Button>
              <Button variant="contained" onClick={handleGroupChange} disabled={loadingGroups}>
                Enregistrer
              </Button>
            </DialogActions>
          </Dialog>
        </Card>
      )}
    </>
  );
};

CustomerBasicDetails.propTypes = {
  name: PropTypes.string,
  user_id: PropTypes.number,
  email: PropTypes.string,
  tlms: PropTypes.number,
  syna: PropTypes.number
};
